// src/pages/PaymentsPending.jsx
import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";

// ─── helpers ────────────────────────────────────────────────────────────────

const toMs = (expiresAt) => {
  if (!expiresAt) return 0;
  if (expiresAt.seconds) return expiresAt.seconds * 1000;
  return Number(expiresAt);
};

const isOtpExpired = (expiresAt) => !expiresAt || Date.now() > toMs(expiresAt);

const formatExpiry = (expiresAt) => {
  const diff = toMs(expiresAt) - Date.now();
  if (diff <= 0) return "Expired";
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}m ${secs}s`;
};

const formatDate = (date) => (date ? date.toLocaleString("en-IN") : "—");

const getAge = (orderTime) => {
  if (!orderTime) return "—";
  const diff = Date.now() - orderTime;
  return `${Math.floor(diff / 60000)}m ${Math.floor((diff % 60000) / 1000)}s`;
};

const getStatusStyle = (status) => ({
  display: "inline-block",
  padding: "2px 6px",
  fontSize: "11px",
  fontWeight: "bold",
  borderRadius: "4px",
  textTransform: "capitalize",
  backgroundColor:
    status === "Requested" ? "#fef3c7" :
    status === "Accepted"  ? "#dcfce7" :
    status === "Assorted"  ? "#ede9fe" : "#e5e7eb",
  color:
    status === "Requested" ? "#92400e" :
    status === "Accepted"  ? "#166534" :
    status === "Assorted"  ? "#5b21b6" : "#374151",
});

// ─── OTP resolution ──────────────────────────────────────────────────────────

/**
 * Fast path: every order in the group gets otpReference written to it
 * by SellDeliver's writeOtpReferenceToOrders the moment the JRE sends the OTP.
 * That field IS the Firestore doc ID in the otps collection.
 */
const getGroupOtpReference = (orders) => {
  for (const order of orders) {
    if (order.otpReference) return order.otpReference;
  }
  return null;
};

/**
 * Broadened fallback — scans the entire otpData map for any
 * OPERATOR_PAYMENT OTP belonging to this group.
 *
 * Matching priority:
 *   1. params.jreId === jreId  (most reliable — always written by SellDeliver)
 *   2. mobile field contains jreMobile ("+91<digits>" stored by cloud)
 *
 * Returns the most-recent doc that has displayOtp set (expired or not —
 * caller decides what to do with expired docs).
 */
const findPaymentOtpByParams = (otpData, buyerId, jreId, jreMobiles) => {
  if (!jreId) return null;

  const rawMobile   = (jreMobiles?.[jreId] || "").toString().replace(/\D/g, "");
  const mobileFull  = rawMobile ? `+91${rawMobile}` : "";

  let bestId   = null;
  let bestDoc  = null;
  let bestTime = -1;

  for (const [id, odoc] of Object.entries(otpData)) {
    // ── Type check ────────────────────────────────────────────────────────
    const uc = (odoc.useCase || "").toUpperCase();
    const tn = (odoc.templateName || "").toUpperCase();
    const isPayment =
      uc === "OPERATOR_PAYMENT" ||
      uc === "JMI_OPERATOR_PAYMENT_OTP" ||
      tn === "JMI_OPERATOR_PAYMENT_OTP";
    if (!isPayment) continue;

    // Must have the plaintext code
    if (!odoc.displayOtp) continue;

    // ── Match by params.jreId ────────────────────────────────────────────
    const pJre   = String(odoc.params?.jreId   || odoc.jreId   || "");
    const pBuyer = String(odoc.params?.buyerId || odoc.params?.targetId || odoc.buyerId || "");

    const byJre   = pJre   === jreId;
    const byBuyer = !buyerId || !pBuyer || pBuyer === buyerId;

    // ── Match by mobile field ────────────────────────────────────────────
    const docMobile  = String(odoc.mobile || "");
    const byMobile   = mobileFull && docMobile === mobileFull;

    const matched = (byJre && byBuyer) || byMobile;

    console.log("🔍 findPaymentOtpByParams checking:", {
      id, uc, pJre, pBuyer, byJre, byBuyer, byMobile, matched,
      displayOtp: odoc.displayOtp,
    });

    if (!matched) continue;

    // Pick most-recent by createdAt
    const t = odoc.createdAt?.seconds ?? 0;
    if (t > bestTime) { bestTime = t; bestId = id; bestDoc = odoc; }
  }

  console.log("🔍 findPaymentOtpByParams result:", bestId || "none");
  return bestId ? { id: bestId, doc: bestDoc } : null;
};

// ─── component ───────────────────────────────────────────────────────────────

const PaymentsPending = () => {
  const [groupedOrders, setGroupedOrders] = useState({});
  const [otpData, setOtpData]             = useState({});
  const [jreMobiles, setJreMobiles]       = useState({});
  
  // 1-second ticker keeps expiry countdowns live
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Live OTP collection listener ──────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "otps"), (snap) => {
      const map = {};
      snap.forEach((d) => { map[d.id] = d.data(); });
      setOtpData(map);
    });
    return unsub;
  }, []);

  // ── Live orders listener — buyerPaid ─────────────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, "orderList"),
      where("orderStatus", "==", "buyerPaid"),
    );
    const unsub = onSnapshot(q, (snap) => {
      const ordersData = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        let orderTime = null;
        if (data.orderTime?.seconds)  orderTime = new Date(data.orderTime.seconds * 1000);
        else if (data.addedAt)        orderTime = new Date(data.addedAt);
        return { docId: docSnap.id, ...data, orderTime };
      });

      const groups = {};
      ordersData.forEach((order) => {
        const buyerId  = order.buyerId  || "Unknown Buyer";
        const jreId    = order.jreId    || "No JRE";
        const groupKey = `${buyerId}__${jreId}`;

        if (!groups[groupKey]) {
          groups[groupKey] = {
            seller:   order.sellerId   || "Unknown Seller",
            sellerId: order.sellerId,
            buyer:    order.buyerName  || order.buyerId || "Unknown Buyer",
            buyerId,
            jreId,
            jreMobile: order.jreMobile || order.jreContact || order.jrePrimaryMobile || "",
            orders: [],
          };
        }
        groups[groupKey].orders.push(order);
      });

      Object.values(groups).forEach((g) =>
        g.orders.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0)),
      );

      setGroupedOrders(groups);
    });
    return unsub;
  }, []);

  // ── Fetch JRE mobiles for all groups ─────────────────────────────────────
  useEffect(() => {
    const missing = [
      ...new Set(
        Object.values(groupedOrders)
          .filter((g) => g.jreId && g.jreId !== "No JRE" && jreMobiles[g.jreId] === undefined)
          .map((g) => g.jreId),
      ),
    ];
    if (!missing.length) return;

    let cancelled = false;
    Promise.all(
      missing.map(async (jreId) => {
        try {
          const snap = await getDoc(doc(db, "jreregistrations", jreId));
          if (!snap.exists()) return [jreId, ""];
          const d = snap.data();
          return [jreId, d.primaryMobile || d.mobileNumber || d.contactNumber || d.phone || d.transactionalMobile || ""];
        } catch { return [jreId, ""]; }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setJreMobiles((prev) => {
        const next = { ...prev };
        entries.forEach(([id, mob]) => { next[id] = mob || null; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [groupedOrders, jreMobiles]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const groupEntries = Object.entries(groupedOrders);

  // All OPERATOR_PAYMENT OTPs in the collection — for debug panel
  // const paymentOtps = Object.entries(otpData).filter(([, d]) =>
  //   d.useCase === "OPERATOR_PAYMENT" || d.useCase === "JMI_OPERATOR_PAYMENT_OTP" || d.templateName === "JMI_OPERATOR_PAYMENT_OTP"
  // );

  return (
    <Layout pageTitle="Payments Pending">
      <div style={{ padding: "16px" }}>


        {groupEntries.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6b7280", paddingTop: "32px", fontSize: "18px" }}>
            No payments pending.
          </p>
        ) : (
          groupEntries.map(([groupKey, group]) => {

            // ── OTP lookup — three-path ──────────────────────────────────
            const otpRefId = getGroupOtpReference(group.orders);
            let otpDoc     = otpRefId ? otpData[otpRefId] : null;
            let resolvedId = otpRefId;

            if (!otpDoc) {
              const fallback = findPaymentOtpByParams(otpData, group.buyerId, group.jreId, jreMobiles);
              if (fallback) { otpDoc = fallback.doc; resolvedId = fallback.id; }
            }

            console.log("💰 PaymentsPending group render:", {
              groupKey,
              buyerId: group.buyerId,
              jreId: group.jreId,
              otpRefId,
              resolvedId,
              otpDoc: otpDoc ? { status: otpDoc.status, displayOtp: otpDoc.displayOtp, expiresAt: otpDoc.expiresAt?.seconds } : null,
              otpDataKeys: Object.keys(otpData).length,
            });

            const visibleOtp    = otpDoc?.displayOtp || "";
            const isVerified    = !!(otpDoc?.verified || otpDoc?.status === "verified");
            const isExpired     = otpDoc ? isOtpExpired(otpDoc.expiresAt) : false;
            const hasValidOtp   = !!(otpDoc && visibleOtp && !isExpired && !isVerified);
            const waitingForOtp = !otpDoc;

            const borderColor =
              hasValidOtp  ? "#f59e0b" :
              isVerified   ? "#10b981" :
              isExpired    ? "#ef4444" : "#6b7280";

            const cardBg =
              hasValidOtp  ? "#fffbeb" :
              isVerified   ? "#f0fdf4" : "#ffffff";

            return (
              <div
                key={groupKey}
                style={{
                  marginBottom: "16px",
                  border: "1px solid #d1d5db",
                  borderLeft: `5px solid ${borderColor}`,
                  borderRadius: "8px",
                  padding: "16px",
                  backgroundColor: cardBg,
                }}
              >
                {/* ── Group header ── */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "16px" }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: "0 0 6px", color: "#1f2937", fontSize: "18px", fontWeight: "bold" }}>
                      {group.buyer}
                    </h3>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ color: "#6b7280", fontSize: "13px" }}>
                        <strong>JRE:</strong> {group.jreId}
                      </span>
                      <span style={{ color: "#6b7280", fontSize: "13px" }}>
                        <strong>Orders:</strong> {group.orders.length}
                      </span>
                      <span style={{ padding: "2px 8px", background: "#fef3c7", color: "#92400e", borderRadius: "12px", fontSize: "11px", fontWeight: "bold" }}>
                        Buyer Paid
                      </span>
                      {resolvedId && (
                        <span style={{ color: "#9ca3af", fontSize: "10px", fontFamily: "monospace" }}>
                          ref: {resolvedId.length > 36 ? resolvedId.slice(0, 36) + "…" : resolvedId}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── OTP badge ── */}
                  <div style={{ minWidth: "260px" }}>
                    {waitingForOtp && (
                      <div style={{ background: "#f3f4f6", border: "1px dashed #9ca3af", color: "#6b7280", padding: "16px", borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "22px", marginBottom: "4px" }}>⏳</div>
                        <div style={{ fontSize: "13px", fontWeight: "600" }}>Waiting for JRE to generate OTP</div>
                        <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.75 }}>Appears here automatically once JRE triggers it</div>
                      </div>
                    )}

                    {hasValidOtp && (
                      <div style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "white", padding: "20px 16px", borderRadius: "8px", textAlign: "center", boxShadow: "0 4px 16px rgba(245,158,11,0.35)" }}>
                        <div style={{ fontSize: "11px", opacity: 0.9, marginBottom: "6px", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Payment OTP
                        </div>
                        <div style={{ fontSize: "40px", fontWeight: "bold", letterSpacing: "12px", fontFamily: "monospace", marginBottom: "10px", textShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                          {visibleOtp}
                        </div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>
                          ⏱ Expires in: <strong>{formatExpiry(otpDoc.expiresAt)}</strong>
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.75, marginTop: "6px" }}>
                          Share with JRE after verifying payment amount
                        </div>
                      </div>
                    )}

                    {isVerified && (
                      <div style={{ background: "#10b981", color: "white", padding: "16px", borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "4px" }}>✅ Payment Verified</div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>
                          {otpDoc?.verifiedAt?.seconds
                            ? new Date(otpDoc.verifiedAt.seconds * 1000).toLocaleString("en-IN")
                            : "Verified"}
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "4px" }}>Orders → Payment Dispatched to Operator</div>
                      </div>
                    )}

                    {!waitingForOtp && isExpired && !isVerified && (
                      <div style={{ background: "#ef4444", color: "white", padding: "16px", borderRadius: "8px", textAlign: "center" }}>
                        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "4px" }}>❌ OTP Expired</div>
                        <div style={{ fontSize: "12px", opacity: 0.9 }}>Ask JRE to generate a new OTP</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Orders table ── */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ minWidth: "100%", borderCollapse: "collapse", border: "1px solid #1f2937" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6" }}>
                        {["Order Time", "Age", "Product", "Image", "Seller", "Net Wt (g)", "Gross Wt (g)", "Status", "Variants"].map((h) => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.orders.map((order) => {
                        const isOverdue = order.orderTime && Date.now() - order.orderTime > 60000;
                        return (
                          <tr key={order.docId} style={{ backgroundColor: isOverdue ? "#fee2e2" : "#ffffff", outline: isOverdue ? "2px solid #fecaca" : "none", outlineOffset: "-2px" }}>
                            <td style={tdStyle}>{formatDate(order.orderTime)}</td>
                            <td style={{ ...tdStyle, fontFamily: "monospace" }}>{getAge(order.orderTime)}</td>
                            <td style={{ ...tdStyle, fontWeight: "bold" }}>{order.productName || "—"}</td>
                            <td style={tdStyle}>
                              {order.images?.[0]?.url
                                ? <img src={order.images[0].url.trim()} alt="Product" style={{ height: "24px", width: "24px", objectFit: "contain", border: "1px solid #d1d5db", borderRadius: "3px" }} />
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                            </td>
                            <td style={tdStyle}>{order.sellerId || "—"}</td>
                            <td style={tdStyle}>{order.netWt || "—"}</td>
                            <td style={tdStyle}>{order.grossWt || "—"}</td>
                            <td style={tdStyle}><span style={getStatusStyle(order.orderStatus)}>{order.orderStatus}</span></td>
                            <td style={tdStyle}>
                              {order.selectedVariants?.length
                                ? order.selectedVariants.map((v, i) => <div key={i} style={{ whiteSpace: "nowrap" }}>{v.size} × {v.quantity}</div>)
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Action banner ── */}
                {hasValidOtp && (
                  <div style={{ marginTop: "12px", padding: "12px 16px", backgroundColor: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "6px", fontSize: "13px", color: "#92400e" }}>
                    <strong>Action required:</strong>
                    <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
                      <li>JRE <strong>{group.jreId}</strong> is arriving with the collected payment for <strong>{group.orders.length}</strong> order{group.orders.length !== 1 ? "s" : ""}.</li>
                      <li>Verify the payment amount matches the orders above.</li>
                      <li>Share OTP <strong style={{ fontSize: "16px", letterSpacing: "4px", fontFamily: "monospace" }}>{visibleOtp}</strong> with the JRE only after verifying.</li>
                      <li>Once verified, orders will be marked as <strong>Payment Dispatched to Operator</strong>.</li>
                    </ul>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
};

// ─── styles ──────────────────────────────────────────────────────────────────

const thStyle = {
  border: "1px solid #1f2937", padding: "4px 8px", textAlign: "left",
  fontSize: "11px", fontWeight: "bold", backgroundColor: "#f3f4f6", color: "#4b5563",
};

const tdStyle = {
  border: "1px solid #1f2937", padding: "4px 8px", fontSize: "12px",
};

export default PaymentsPending;
